import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, CheckCircle2 } from "lucide-react";

export default function Appeal() {
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (message.trim().length < 20) { toast.error("Please write at least 20 characters"); return; }
        setBusy(true);
        try {
            await api.post("/appeals", {
                email: email.trim(),
                username: username.trim() || undefined,
                message: message.trim(),
            });
            setSubmitted(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="max-w-lg mx-auto px-4 py-8 sm:py-12" data-testid="page-appeal">
            <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold hover:underline mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg brut-static bg-[#FFB8D1] flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                    <h1 className="display text-3xl font-black">Ban appeal</h1>
                    <p className="text-sm text-[#4A4A4A] font-medium">Think we got it wrong? Tell us what happened.</p>
                </div>
            </div>

            {submitted ? (
                <div className="bg-white brut-static rounded-xl p-6 text-center" data-testid="appeal-success">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-3" />
                    <h2 className="display text-2xl font-black mb-1">Appeal received.</h2>
                    <p className="text-sm text-[#4A4A4A] font-medium">A human will review and email you back within 5 business days.</p>
                    <Link to="/" className="mt-5 inline-block px-4 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold">Back to home</Link>
                </div>
            ) : (
                <form onSubmit={submit} className="bg-white brut-static rounded-xl p-6 space-y-4" data-testid="appeal-form">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">Email</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none"
                            data-testid="appeal-email"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">Username (if you remember it)</label>
                        <input
                            value={username}
                            onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                            maxLength={30}
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none"
                            data-testid="appeal-username"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">What happened?</label>
                        <textarea
                            required
                            minLength={20}
                            maxLength={2000}
                            rows={6}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Tell us the context. We'll take a fresh look."
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none text-sm"
                            data-testid="appeal-message"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={busy}
                        className="w-full px-4 py-3 rounded-lg brut bg-[#0A0A0A] text-white font-bold disabled:opacity-50"
                        data-testid="appeal-submit"
                    >
                        {busy ? "Submitting…" : "Submit appeal"}
                    </button>
                    <p className="text-[11px] text-[#4A4A4A] font-medium text-center">
                        Appeals limited to 3 per email address. We take every one seriously.
                    </p>
                </form>
            )}
        </main>
    );
}
